// eyeq — экран «План» (менеджерская матрица заполненности ячеек) + реестр
// ячеек eyeq_cells (миграция 0029, ADR-0072).
//
// Здесь же живёт ЕДИНСТВЕННОЕ определение «закрыта ли ячейка» —
// eyeqCellClosure: чистая функция от счётчиков реестра и офлайн-снапшота.
// Статус закрытия сознательно НЕ хранится в D1: он зависит от
// eyeq_dev_overlay.offline_pending, у которого свой ритм пуша, и хранимая
// колонка протухала бы при каждом пуше. Воркер (drawer) и фронт (матрица)
// вызывают одну и ту же функцию над одними и теми же числами.
import { z } from 'zod';
import { EYEQ_CELL_CLOSURES, EYEQ_CELL_CHANNELS, type EyeqCellClosure } from '../vocab/eyeq';
import { EyeqQuotaModeSchema } from './quota';
import { EyeqAssetStatusSchema } from './assets';
import { EyeqClientStatusSchema } from './delivery';

// ── cat_id slug ─────────────────────────────────────────────────────────────
// Перенесено из apps/eyeq-api/src/indexer.ts, чтобы модуль реестра ячеек и
// фронт могли строить слаг без циклического импорта. Правило ВЕЧНОЕ: оно
// запечено в сид 0004_seed_taxonomy и в каждый r2_key — менять нельзя.
const eyeqSlug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
export const eyeqCatNodeId = (category: string, subCategory: string): string =>
  `${eyeqSlug(category)}__${eyeqSlug(subCategory)}`;

// ── счётчики ячейки и закрытие ──────────────────────────────────────────────
export interface EyeqCellCounters {
  /** effective-план ячейки; null = строки в eyeq_quota нет вовсе (0 ≠ null). */
  plan: number | null;
  shipped_people: number;
  shipped_cards: number;
  qa_people: number;
  qa_cards: number;
  client_accepted: number;
  client_rejected: number;
  /** Кадры офлайн-конвейера, ещё не залитые в R2; null = конвейер не сообщал
   *  этого числа (старый push-скрипт) — состояние 'offline' тогда не
   *  заявляется вовсе. eval_clean сюда не годится: пересекается с уже залитым
   *  интейком и даёт двойной счёт. */
  offline_pending: number | null;
}

/** Валовая отгрузка: байты у заказчика, лог неоткатываем. */
export function eyeqCellShipped(c: EyeqCellCounters): number {
  return c.shipped_people + c.shipped_cards;
}

/** Стоящая отгрузка: валовая минус клиентские реджекты. Ей меряется прогресс
 *  и закрытие — план не удовлетворён материалом, который заказчик выбросил.
 *  (Противоположно ADR-0059: там КАП — реджект освобождает слот; тут ПЛАН.) */
export function eyeqCellNetShipped(c: EyeqCellCounters): number {
  return Math.max(0, eyeqCellShipped(c) - c.client_rejected);
}

/** Материал в процессе: в D1, не отгружен, не rejected/duplicate. */
export function eyeqCellQa(c: EyeqCellCounters): number {
  return c.qa_people + c.qa_cards;
}

export function eyeqCellClosure(c: EyeqCellCounters): EyeqCellClosure {
  const p = c.plan;
  if (p === null) return '';
  const n = eyeqCellNetShipped(c);
  const q = eyeqCellQa(c);
  if (c.client_accepted >= p) return 'client';
  if (n >= p) return 'shipped';
  if (n + q >= p) return 'shipped_qa';
  if (c.offline_pending !== null && n + q + c.offline_pending >= p) return 'offline';
  return 'open';
}

/** remainder — сколько ещё ОТГРУЗИТЬ; remainder_qa — сколько ещё СОБРАТЬ. */
export function eyeqCellRemainder(c: EyeqCellCounters): { remainder: number | null; remainder_qa: number | null } {
  if (c.plan === null) return { remainder: null, remainder_qa: null };
  const n = eyeqCellNetShipped(c);
  return {
    remainder: Math.max(0, c.plan - n),
    remainder_qa: Math.max(0, c.plan - n - eyeqCellQa(c)),
  };
}

// ── GET /api/eyeq/manager/plan — матрица ────────────────────────────────────
// Компакт-ключи по прецеденту dev.ts: ~3.4k ячеек в одном ответе. Нули сервер
// ОПУСКАЕТ, .default(0) восстанавливает их на парсе (правило 4
// .claude/rules/contracts.md). p эмитится ВСЕГДА, когда строка квоты есть
// (даже 0) — отсутствие ключа значит «нет квоты», и это другой случай.
//   l/c/s ячейка · p план effective · pb baseline (только когда ≠ p) ·
//   ur url_required · sp/sc отгружено люди/карты · qp/qc в процессе ·
//   qa ручной accept из qp+qc · qu из qa со ссылкой · ca/cr вердикты
//   заказчика · pn разных участников · op офлайн-не-в-R2 (null = не
//   сообщалось) · ec eval_clean конвейера (контекст, не доказательство).
// Закрытие/остатки клиент считает сам через eyeqCellClosure/…Remainder —
// все входы в ответе есть, а 3.4k enum-строк в JSON не нужны.
export const EyeqPlanCellSchema = z.object({
  l: z.string(),
  c: z.string(),
  s: z.string(),
  p: z.number().int().nullable().default(null),
  pb: z.number().int().nullable().default(null),
  ur: z.number().int().default(0),
  sp: z.number().int().default(0),
  sc: z.number().int().default(0),
  qp: z.number().int().default(0),
  qc: z.number().int().default(0),
  qa: z.number().int().default(0),
  qu: z.number().int().default(0),
  ca: z.number().int().default(0),
  cr: z.number().int().default(0),
  pn: z.number().int().default(0),
  op: z.number().int().nullable().default(null),
  ec: z.number().int().default(0),
});
export type EyeqPlanCell = z.infer<typeof EyeqPlanCellSchema>;

/** Компакт-ячейка → счётчики для eyeqCellClosure. Одно место маппинга. */
export function eyeqPlanCellCounters(cell: EyeqPlanCell): EyeqCellCounters {
  return {
    plan: cell.p,
    shipped_people: cell.sp,
    shipped_cards: cell.sc,
    qa_people: cell.qp,
    qa_cards: cell.qc,
    client_accepted: cell.ca,
    client_rejected: cell.cr,
    offline_pending: cell.op,
  };
}

export const EyeqPlanResSchema = z.object({
  ok: z.literal(true),
  built_at: z.string(),   // MAX(eyeq_cells.updated_at); '' до первого reconcile
  overlay_at: z.string(), // MAX(eyeq_dev_overlay.updated_at); '' до первого пуша
  cells: z.array(EyeqPlanCellSchema),
  /** Порядок таксономии (категории → подкатегории, в порядке сида 0004) —
   *  дефолтная сортировка экрана «по таксономии»; реестр упорядочен PK. */
  tax: z.array(z.object({ c: z.string(), s: z.array(z.string()) })).default([]),
});
export type EyeqPlanRes = z.infer<typeof EyeqPlanResSchema>;

// ── GET /api/eyeq/manager/plan/cell — ящик одной ячейки ─────────────────────
export const EyeqPlanCellQuerySchema = z.object({
  locale: z.string().min(1).max(16),
  category: z.string().min(1).max(64),
  sub_category: z.string().min(1).max(64),
  after: z.string().max(40).default(''),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type EyeqPlanCellQuery = z.infer<typeof EyeqPlanCellQuerySchema>;

export const EyeqCellChannelSchema = z.enum(EYEQ_CELL_CHANNELS);
export const EyeqCellClosureSchema = z.enum(EYEQ_CELL_CLOSURES);

export const EyeqPlanCellAssetSchema = z.object({
  asset_id: z.string(),
  ch: EyeqCellChannelSchema,
  status: EyeqAssetStatusSchema,
  qa_verdict: z.string(),
  reject_reason: z.string(),
  ship_issue: z.string(),
  client_status: EyeqClientStatusSchema,
  /** 1 = у кадра есть превью-сиблинги в R2; 0 = открытие потянет оригинал. */
  preview: z.number().int(),
  created_at: z.string(),
});
export type EyeqPlanCellAsset = z.infer<typeof EyeqPlanCellAssetSchema>;

export const EyeqPlanCellParticipantSchema = z.object({
  public_id: z.string(), // ep_<12hex>; uid не покидает воркер (ADR-0040)
  total: z.number().int(),
  shipped: z.number().int(),
  in_work: z.number().int(),
  rejected: z.number().int(),
  first_at: z.string(),
  last_at: z.string(),
});
export type EyeqPlanCellParticipant = z.infer<typeof EyeqPlanCellParticipantSchema>;

export const EyeqPlanCellResSchema = z.object({
  ok: z.literal(true),
  cell: z.object({ locale: z.string(), category: z.string(), sub_category: z.string() }),
  plan: z.object({
    baseline: z.number().int().nullable(),
    effective: z.number().int().nullable(),
    url_required: z.boolean(),
    mode: EyeqQuotaModeSchema,
    floor: z.number().int().nullable(),
    cap: z.number().int().nullable(),
  }),
  counts: z.object({
    shipped_people: z.number().int(),
    shipped_cards: z.number().int(),
    qa_people: z.number().int(),
    qa_cards: z.number().int(),
    qa_accepted: z.number().int(),
    qa_accepted_url: z.number().int(),
    client_accepted: z.number().int(),
    client_rejected: z.number().int(),
    people_n: z.number().int(),
  }),
  closure: EyeqCellClosureSchema,
  remainder: z.number().int().nullable(),
  remainder_qa: z.number().int().nullable(),
  offline: z.object({
    pending: z.number().int().nullable(),
    eval_clean: z.number().int(),
    eval_total: z.number().int(),
    links_linked: z.number().int(),
    links_na: z.number().int(),
    links_needs_human: z.number().int(),
    at: z.string(),
  }).nullable(),
  participants: z.array(EyeqPlanCellParticipantSchema),
  assets: z.array(EyeqPlanCellAssetSchema),
  assets_total: z.number().int(),
  next_after: z.string(), // '' = хвоста нет; keyset по asset_id, не offset
});
export type EyeqPlanCellRes = z.infer<typeof EyeqPlanCellResSchema>;

// ── GET /api/eyeq/manager/asset/:asset_id/history — таймлайн ассета ─────────
export const EyeqAssetEventSourceSchema = z.enum(['intrinsic', 'audit', 'delivery']);

export const EyeqAssetEventSchema = z.object({
  at: z.string(),
  source: EyeqAssetEventSourceSchema,
  action: z.string(),
  /** public_id участника, либо machine-/intake-лейбл — никогда не uid. */
  actor: z.string(),
  detail: z.record(z.string(), z.unknown()),
});
export type EyeqAssetEvent = z.infer<typeof EyeqAssetEventSchema>;

export const EyeqAssetHistoryResSchema = z.object({
  ok: z.literal(true),
  asset_id: z.string(),
  asset: z.object({
    locale: z.string(),
    category: z.string(),
    sub_category: z.string(),
    ai_category: z.string(),
    ai_sub_category: z.string(),
    ch: EyeqCellChannelSchema,
    uploader_public_id: z.string(),
    status: z.string(),
    qa_verdict: z.string(),
    reject_reason: z.string(),
    ship_issue: z.string(),
    shipment_id: z.string(),
    cat_id_matches_meta: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  }),
  events: z.array(EyeqAssetEventSchema),
});
export type EyeqAssetHistoryRes = z.infer<typeof EyeqAssetHistoryResSchema>;

// ── reconcile (machine /cells/reconcile + менеджерский plan/reconcile) ──────
export const EyeqCellsReconcileReqSchema = z.object({
  scope: z.enum(['all', 'locale']).default('all'),
  locale: z.string().max(16).default(''),
  fix_cat_id: z.boolean().default(false),
  /** Машинный вызов подписывается сам; менеджерский твин берёт uid сессии. */
  actor: z.string().max(80).default(''),
});
export type EyeqCellsReconcileReq = z.infer<typeof EyeqCellsReconcileReqSchema>;

export const EyeqCellsReconcileResSchema = z.object({
  ok: z.literal(true),
  scope: z.string(),
  cells_written: z.number().int(),
  /** Ассеты вне ячеек (нет меты): честное число, а не тихий ноль. */
  unassigned: z.record(z.string(), z.number().int()),
  cat_id_drift: z.number().int(),
  cat_id_fixed: z.number().int(),
  ms: z.number().int(),
});
export type EyeqCellsReconcileRes = z.infer<typeof EyeqCellsReconcileResSchema>;
